import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import Popover from './components/Popover';
import Scenarios from './components/Scenarios';

import { calculateProfitAfterTax, calculateProfitBeforeTax } from './helpers/calculations';

import './assets/styles/main.scss';

const LiquidityCalculator = () => {
  const { register, handleSubmit, reset } = useForm({ defaultValues: { section102: 'y' } });

  const [popover, setPopover] = useState('');

  const [profit, setProfit] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);

  useEffect(() => {
    if (profit) setShowResults(true);
  }, [profit]);

  const onSubmit = (data) => {
    setIsFormValid(true);

    data.date = new Date(data.date);
    const today = new Date();
    const diffInTime = today.getTime() - data.date.getTime();
    const diffInYears = diffInTime / (1000 * 3600 * 24) / 365;

    const profitBeforeTax = calculateProfitBeforeTax(data.ec, data.p, data.q);
    const profitAfterTax = calculateProfitAfterTax(profitBeforeTax, data.section102, diffInYears, data.tax);

    if (!isNaN(profitBeforeTax) && !isNaN(profitAfterTax)) {
      setProfit({
        beforeTax: profitBeforeTax,
        afterTax: profitAfterTax,
        data,
        diffInYears
      });
    } else {
      setIsFormValid(false);
    }
  };

  const renderForm = () => {
    if (!showResults) {
      return (
        <>
          <form onSubmit={handleSubmit(onSubmit)} id='calculator_form'>
            <div className='mb-4 text-center card-header text-white bg-green'>מחשבון: כמה תרוויחו באקזיט?</div>
            <div className='mb-3 row'>
              <div className='col-12 col-sm-10 col-md-8 col-lg-7 col-xl-6'>
                <label htmlFor='html-q' className='form-label d-flex align-items-center'>
                  כמות אופציות שהבשילו או מומשו:
                  <Popover
                    isOpen={popover === 'popover-q'}
                    content='אם אתם מועסקים בחברה - הזינו את כמות האופציות שתבשיל במועד האקזיט. אם כבר עזבתם את החברה, הכניסו את כמות האופציות שמימשתם.'
                  >
                    <i
                      className='ms-1 far fa-question-circle'
                      onMouseOver={() => setPopover('popover-q')}
                      onMouseOut={() => setPopover('')}
                    />
                  </Popover>
                </label>
                <input type='number' id='html-q' className='form-control' {...register('q', {})} />
              </div>
            </div>
            <div className='mb-3 row'>
              <div className='col-12 col-sm-10 col-md-8 col-lg-7 col-xl-6'>
                <label htmlFor='html-ec' className='form-label d-flex align-items-center'>
                  מחיר מימוש לאופציה במענק:
                  <Popover
                    isOpen={popover === 'popover-ec'}
                    content='מחיר המימוש לאופציה יופיע בהסכם האופציות שלכם, הידוע גם בשמות Grant Letter. המונח שיופיע בהסכם הוא Exercise Price or Strike Price'
                  >
                    <i
                      className='ms-1 far fa-question-circle'
                      onMouseOver={() => setPopover('popover-ec')}
                      onMouseOut={() => setPopover('')}
                    />
                  </Popover>
                </label>
                <div className='input-group'>
                  <input type='number' id='html-ec' step={0.01} className='form-control' {...register('ec', {})} />
                  <div className='input-group-append'>
                    <span className='input-group-text' id='basic-addon2'>
                      $
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className='mb-3 row'>
              <div className='col-12 col-sm-10 col-md-8 col-lg-7 col-xl-6'>
                <label htmlFor='html-p' className='form-label d-flex align-items-center'>
                  מחיר למניה באקזיט:
                  <Popover
                    isOpen={popover === 'popover-p'}
                    content='תוכלו לשאול את מחלקת הכספים של החברה או את נאמן האופציות של החברה שמחזיק במניות. במקרה של הנפקה הזינו את המחיר בו המניה צפויה להמכר.'
                  >
                    <i
                      className='ms-1 far fa-question-circle'
                      onMouseOver={() => setPopover('popover-p')}
                      onMouseOut={() => setPopover('')}
                    />
                  </Popover>
                </label>
                <div className='input-group'>
                  <input type='number' id='html-p' className='form-control' step={0.01} {...register('p', {})} />
                  <div className='input-group-append'>
                    <span className='input-group-text' id='basic-addon2'>
                      $
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className='mb-3 row'>
              <div className='col-12 col-sm-10 col-md-8 col-lg-7 col-xl-6'>
                <label className='form-label d-flex align-items-center'>
                  האם האופציות הוענקו תחת סעיף 102 לפקודת מס הכנסה?
                  <Popover isOpen={popover === 'popover-section102'} content='המידע יופיע בהסכם האופציות שלכם'>
                    <i
                      className='ms-1 far fa-question-circle'
                      onMouseOver={() => setPopover('popover-section102')}
                      onMouseOut={() => setPopover('')}
                    />
                  </Popover>
                </label>
                <div className='form-check'>
                  <input className='form-check-input' id='section102y' value='y' {...register('section102')} type='radio' />
                  <label className='form-check-label' htmlFor='section102y'>
                    כן
                  </label>
                </div>

                <div className='form-check'>
                  <input className='form-check-input' id='section102n' {...register('section102')} type='radio' value='n' />
                  <label className='form-check-label' htmlFor='section102n'>
                    לא
                  </label>
                </div>
                <div className='form-check'>
                  <input className='form-check-input' id='section102dk' {...register('section102')} type='radio' value='y' />
                  <label className='form-check-label' htmlFor='section102dk'>
                    לא יודע/ת
                  </label>
                </div>
              </div>
            </div>
            <div className='mb-3 row'>
              <div className='col-12 col-sm-10 col-md-8 col-lg-7 col-xl-6'>
                <label htmlFor='html-date' className='form-label'>
                  מועד הענקת האופציות:
                  <Popover
                    isOpen={popover === 'popover-date'}
                    content='הכוונה למועד ממנו התחילו האופציות להבשיל (Commencement Date). המידע ישמש אותנו לחישוב שיעור המס באקזיט.'
                  >
                    <i
                      className='ms-1 far fa-question-circle'
                      onMouseOver={() => setPopover('popover-date')}
                      onMouseOut={() => setPopover('')}
                    />
                  </Popover>
                </label>
                <input
                  type='date'
                  id='html-date'
                  className='form-control'
                  max={new Date().toISOString().split('T')[0]}
                  {...register('date', {})}
                />
              </div>
            </div>
            <div className='mb-4 row'>
              <div className='col-12 col-sm-10 col-md-8 col-lg-7 col-xl-6'>
                <label htmlFor='html-tax' className='form-label'>
                  שיעור המס השולי שלך (מס הכנסה):
                  <Popover isOpen={popover === 'popover-tax'} content='המידע ישמש אותנו לחישוב הרווח אחרי מס באקזיט.'>
                    <i
                      className='ms-1 far fa-question-circle'
                      onMouseOver={() => setPopover('popover-tax')}
                      onMouseOut={() => setPopover('')}
                    />
                  </Popover>
                </label>
                <select id='html-tax' className='form-control' {...register('tax')}>
                  <option value='10'>10%</option>
                  <option value='14'>14%</option>
                  <option value='20'>20%</option>
                  <option value='31'>31%</option>
                  <option value='35'>35%</option>
                  <option value='47'>47%</option>
                  <option value='50'>50%</option>
                </select>
              </div>
            </div>

            {!isFormValid && <div className='alert-danger p-2 mb-2'>Please check that all fields are filled in</div>}

            <div className='card-footer'>
              <input type='submit' className='btn btn-success' value='חשב' />
            </div>
          </form>
        </>
      );
    }
  };

  const renderResults = () => {
    if (showResults) {
      return (
        <>
          <div className='mb-4 text-center card-header text-white bg-green'>מחשבון: כמה תרוויחו באקזיט?</div>
          <div className='text-center'>
            <div className='lead mb-3'>
              <div>רווח לפני מס</div>
              <h3 className='display-4 mb-0 color-green'>${new Intl.NumberFormat('en-US').format(profit.beforeTax)}</h3>
            </div>
            <div className='lead mb-5'>
              <div>רווח אחרי מס</div>
              <h3 className='display-4 mb-0 color-green'>
                ${new Intl.NumberFormat('en-US').format(profit.afterTax)}
                <Popover
                  isOpen={popover === 'popover-profit'}
                  content='עבור מניות שעומדות בתנאים לסעיף 102 השתמשנו במס רווח הון בשיעור 25%, ביתר המקרים החישוב נעשה לפי שיעור-המס הפירותי השולי'
                >
                  <small>
                    <i
                      className='ms-1 far fa-question-circle'
                      onMouseOver={() => setPopover('popover-profit')}
                      onMouseOut={() => setPopover('')}
                    />
                  </small>
                </Popover>
              </h3>
            </div>

            <div className='mb-4'>
              <h4>והנה עוד כמה תרחישים שאולי יכולים לקרות:</h4>
              <Scenarios data={profit.data} diffInYears={profit.diffInYears} />
            </div>

            <div className='lc-sum-again'>
              <h3 className='display-8'>קיבלתם יותר ממענק אופציות אחד?</h3>
              <p>בצעו את הבדיקה עבור כל אחת מחבילות האופציות כדי לחשב את סך הרווח שלכם באקזיט</p>

              <div>
                <button onClick={(e) => resetForm(e)} className='btn btn-success'>
                  להתחיל מחדש
                </button>
              </div>
            </div>
          </div>
        </>
      );
    }
  };

  const resetForm = (e) => {
    e.preventDefault();
    setShowResults(false);
    reset();
  };

  return (
    <div className='liquidity-calculator container-md px-1 px-sm-3'>
      {renderForm()}
      {renderResults()}
    </div>
  );
};

export default LiquidityCalculator;
